import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  VStack,
  Heading,
  Text,
  Button,
  Alert,
  Input,
  Spinner,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { supabase } from '@/lib/supabase'
import { CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react'
import { ChakraProvider, createSystem, defaultConfig } from '@chakra-ui/react'
import { generateColorPalette } from '@/shared/utils/colorPalette'

interface AgencyData {
  id: string
  name: string
  brand_primary_color: string | null
  logo_inline_url: string | null
}

interface WelcomeContentProps {
  agency: AgencyData
}

export function WelcomePage() {
  const [searchParams] = useSearchParams()
  const [agency, setAgency] = useState<AgencyData | null>(null)
  const [loadingAgency, setLoadingAgency] = useState(true)
  const [agencyError, setAgencyError] = useState<string | null>(null)

  useEffect(() => {
    // Charger l'agence de l'utilisateur
    const loadAgency = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (!session?.user) {
          const error = searchParams.get('error')
          if (error) {
            setAgencyError('Lien d\'invitation invalide ou expiré. Veuillez contacter votre administrateur.')
          } else {
            setAgencyError('Session non trouvée. Veuillez cliquer à nouveau sur le lien d\'invitation.')
          }
          setLoadingAgency(false)
          return
        }

        // Récupérer l'utilisateur avec son agency_id
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', session.user.id)
          .single()

        if (userError || !userData?.agency_id) {
          setAgencyError('Erreur critique : Aucune agence associée à cet utilisateur. Contactez l\'administrateur système.')
          setLoadingAgency(false)
          return
        }

        // Récupérer les données de l'agence
        const { data: agencyData, error: agencyError } = await supabase
          .from('agencies')
          .select('id, name, brand_primary_color, logo_inline_url')
          .eq('id', userData.agency_id)
          .single()

        if (agencyError || !agencyData) {
          setAgencyError('Erreur critique : Agence introuvable dans la base de données. Contactez l\'administrateur système.')
          setLoadingAgency(false)
          return
        }

        console.log('Agency data loaded:', agencyData)
        setAgency(agencyData)
        setLoadingAgency(false)
      } catch (error) {
        console.error('Error loading agency:', error)
        setAgencyError('Erreur lors du chargement des données de l\'agence.')
        setLoadingAgency(false)
      }
    }

    loadAgency()
  }, [searchParams])

  // Render - always use ChakraProvider
  // Generate palette (use default if no agency yet)
  const palette = (agency && agency.brand_primary_color) ? generateColorPalette(agency.brand_primary_color) : {
    50: '#f3fcf9',
    100: '#e2f8f2',
    200: '#c6f5e8',
    300: '#7ff0d0',
    400: '#12cc98',
    500: '#065F46',
    600: '#064b38',
    700: '#063b2c',
    800: '#062c22',
    900: '#06221a',
    950: '#061813',
  }

  const system = createSystem(defaultConfig, {
    theme: {
      tokens: {
        colors: {
          brand: {
            50: { value: palette[50] },
            100: { value: palette[100] },
            200: { value: palette[200] },
            300: { value: palette[300] },
            400: { value: palette[400] },
            500: { value: palette[500] },
            600: { value: palette[600] },
            700: { value: palette[700] },
            800: { value: palette[800] },
            900: { value: palette[900] },
            950: { value: palette[950] },
          },
        },
      },
    },
  })

  // Couleur de base pour les états de chargement/erreur
  const baseColor = agency?.brand_primary_color || palette[500]

  return (
    <ChakraProvider value={system}>
      {loadingAgency ? (
        <Box
          minH="100vh"
          background={`linear-gradient(135deg, ${baseColor} 0%, color-mix(in srgb, ${baseColor} 70%, black) 100%)`}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack gap={4}>
            <Spinner size="xl" color="white" />
            <Text color="white" fontSize="lg" fontWeight="medium">Chargement...</Text>
          </VStack>
        </Box>
      ) : agencyError || !agency ? (
        <Box
          minH="100vh"
          background={`linear-gradient(135deg, ${baseColor} 0%, color-mix(in srgb, ${baseColor} 70%, black) 100%)`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Card.Root maxW="md" width="full" boxShadow="2xl">
            <Card.Body p={8}>
              <VStack gap={4}>
                <Box color="red.500">
                  <AlertCircle size={48} />
                </Box>
                <Heading size="lg" textAlign="center">
                  Erreur d'accès
                </Heading>
                <Text textAlign="center" color="gray.600">
                  {agencyError}
                </Text>
                <Button
                  colorPalette="red"
                  onClick={() => window.location.href = '/app/login'}
                  size="lg"
                  w="full"
                >
                  Retour à la connexion
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Box>
      ) : (
        <WelcomeContent agency={agency} />
      )}
    </ChakraProvider>
  )
}

function WelcomeContent({ agency }: WelcomeContentProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [userEmail, setUserEmail] = useState<string>('')

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  })

  useEffect(() => {
    // Récupérer l'email de l'utilisateur
    const getEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        setUserEmail(session.user.email || '')
      }
    }
    getEmail()
  }, [])

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule'
    }
    if (!/[a-z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule'
    }
    if (!/[0-9]/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas')
      return
    }

    const passwordError = validatePassword(formData.password)
    if (passwordError) {
      setError(passwordError)
      return
    }

    setLoading(true)

    try {
      // Mettre à jour le mot de passe
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password,
      })

      if (updateError) throw updateError

      // Marquer l'invitation comme acceptée
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase
          .from('users')
          .update({
            invitation_status: 'active',
            invitation_accepted_at: new Date().toISOString(),
          })
          .eq('id', user.id)
      }

      setSuccess(true)

      // Redirection après 2 secondes
      setTimeout(() => {
        navigate('/intranet/dashboard')
      }, 2000)
    } catch (err) {
      console.error('Error setting password:', err)
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    if (strength <= 2) return { strength: 33, label: 'Faible', color: 'red.500' }
    if (strength <= 4) return { strength: 66, label: 'Moyen', color: 'orange.500' }
    return { strength: 100, label: 'Fort', color: 'green.500' }
  }

  const passwordStrength = formData.password ? getPasswordStrength(formData.password) : null

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      background={`linear-gradient(135deg, ${agency.brand_primary_color} 0%, color-mix(in srgb, ${agency.brand_primary_color} 70%, black) 100%)`}
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        opacity: 0.15,
        background: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%), radial-gradient(circle at 80% 80%, white 0%, transparent 50%)',
        pointerEvents: 'none',
      }}
    >
      {/* Logo Header */}
      <Box py={8} textAlign="center" position="relative" zIndex={1}>
        {agency.logo_inline_url ? (
          <Box
            display="inline-block"
            bg="white"
            p={4}
            borderRadius="xl"
            boxShadow="lg"
          >
            <img
              src={agency.logo_inline_url}
              alt={`Logo ${agency.name}`}
              style={{
                height: '60px',
                maxWidth: '240px',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : (
          <Heading size="2xl" color="white" fontFamily="heading" textShadow="0 2px 4px rgba(0,0,0,0.2)">
            {agency.name}
          </Heading>
        )}
      </Box>

      {/* Main Content */}
      <Box
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        position="relative"
        zIndex={1}
      >
      <Card.Root maxW="500px" w="full" boxShadow="2xl" borderWidth="1px" borderColor="whiteAlpha.300" bg="white">
        <Card.Body p={8}>
          <VStack gap={6} align="stretch">
            {/* Header */}
            <Box textAlign="center">
              <Heading size="xl" mb={2} color="brand.700">
                Bienvenue chez {agency.name}
              </Heading>
              <Text color="gray.600" fontSize="md">
                Créez votre mot de passe pour accéder à votre espace
              </Text>
              {userEmail && (
                <Text color="gray.500" fontSize="sm" mt={2}>
                  {userEmail}
                </Text>
              )}
            </Box>

            {/* Succès */}
            {success && (
              <Alert.Root status="success" borderRadius="md">
                <Alert.Indicator>
                  <CheckCircle />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Title>Mot de passe créé avec succès !</Alert.Title>
                  <Alert.Description>
                    Vous allez être redirigé vers votre tableau de bord...
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {/* Erreur */}
            {error && (
              <Alert.Root status="error" borderRadius="md">
                <Alert.Indicator>
                  <AlertTriangle />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Title>Erreur</Alert.Title>
                  <Alert.Description>{error}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {/* Formulaire */}
            {!success && userEmail && (
              <form onSubmit={handleSubmit}>
                <VStack gap={4} align="stretch">
                  {/* Mot de passe */}
                  <Field label="Mot de passe" required>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="Entrez votre mot de passe"
                      required
                    />
                    {passwordStrength && (
                      <Box mt={2}>
                        <Box
                          h="4px"
                          w="full"
                          bg="gray.200"
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            h="full"
                            w={`${passwordStrength.strength}%`}
                            bg={passwordStrength.color}
                            transition="all 0.3s ease"
                          />
                        </Box>
                        <Text fontSize="xs" color={passwordStrength.color} mt={1}>
                          Force : {passwordStrength.label}
                        </Text>
                      </Box>
                    )}
                  </Field>

                  {/* Confirmation */}
                  <Field label="Confirmer le mot de passe" required>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, confirmPassword: e.target.value })
                      }
                      placeholder="Confirmez votre mot de passe"
                      required
                    />
                  </Field>

                  {/* Exigences */}
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                      Exigences du mot de passe :
                    </Text>
                    <VStack gap={1} align="stretch" fontSize="xs" color="gray.600">
                      <Text>✓ Au moins 8 caractères</Text>
                      <Text>✓ Une majuscule et une minuscule</Text>
                      <Text>✓ Au moins un chiffre</Text>
                      <Text color="gray.500">• Un caractère spécial (recommandé)</Text>
                    </VStack>
                  </Box>

                  {/* Bouton submit */}
                  <Button
                    type="submit"
                    colorPalette="brand"
                    size="lg"
                    w="full"
                    loading={loading}
                    mt={2}
                  >
                    Créer mon mot de passe
                  </Button>
                </VStack>
              </form>
            )}

            {/* Lien invalide */}
            {!success && !userEmail && (
              <Box textAlign="center">
                <Text color="gray.600" mb={4}>
                  Le lien d'invitation semble invalide ou a expiré.
                </Text>
                <Button
                  variant="outline"
                  onClick={() => navigate('/auth/login')}
                  w="full"
                >
                  Retour à la connexion
                </Button>
              </Box>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
      </Box>

      {/* Footer minimal */}
      <Box py={6} textAlign="center" fontSize="sm" color="whiteAlpha.900" position="relative" zIndex={1}>
        © {new Date().getFullYear()} {agency.name}. Propulsé par Kopro.
      </Box>
    </Box>
  )
}
